/**
 * The graph's corner controls: fit, and the guide.
 *
 * **Desktop** keeps its zoom percentage bottom-left (it is also the fit
 * button, §2.10 #25 - see `GraphZoomBadge`) and the round help button
 * bottom-right.
 *
 * **A phone** gets a column of two 44px round buttons in the TOP-right. The
 * bottom of its canvas belongs to the concept peek card, which would cover
 * both, and a percentage tells a pinching thumb nothing: what the badge is
 * really for is getting back to the whole map, so here it is the fit icon and
 * says so. Top-right is also where map apps keep their view controls, clear
 * of the thumb that is panning.
 */

import { HelpCircle, Maximize2, Minus, Plus } from 'lucide-react';
import { GraphZoomBadge } from '@/components/features/mindspace/graph/GraphZoomBadge';
import { GraphLegend } from '@/components/features/mindspace/graph/GraphLegend';

/** One segment of the desktop toolbar: square, quiet, a ring on focus. */
const SEGMENT =
  'inline-flex h-8 w-8 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';

const ROUND =
  'flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card/90 text-foreground shadow-elevation-1 backdrop-blur transition-transform duration-fast active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:active:scale-100';

interface GraphControlsProps {
  phone: boolean;
  /** Current canvas scale, for the desktop badge. */
  zoom: number;
  onFit: () => void;
  onHelp: () => void;
  /** Step the zoom by a factor (desktop +/−). */
  onZoomBy?: (factor: number) => void;
  /** Add the paused row to the Legend. */
  showPaused?: boolean;
}

/** How far one press of + or − zooms. */
export const ZOOM_STEP = 1.4;

export function GraphControls({
  phone,
  zoom,
  onFit,
  onHelp,
  onZoomBy,
  showPaused = false,
}: GraphControlsProps) {
  if (!phone) {
    return (
      <>
        {/* One toolbar for everything about the VIEW: what the colours mean,
            and how close you are. It used to be a Legend button and a zoom
            badge stacked separately, with no way to zoom without a wheel or
            trackpad. The % is still the fit button. */}
        <div
          role='toolbar'
          aria-label='Map view'
          className='absolute bottom-6 left-6 flex items-center divide-x divide-border overflow-hidden rounded-lg border border-border bg-card/90 shadow-sm backdrop-blur-sm'
        >
          <GraphLegend showPaused={showPaused} />
          <div className='flex items-center'>
            <button
              type='button'
              aria-label='Zoom out'
              className={SEGMENT}
              onClick={() => onZoomBy?.(1 / ZOOM_STEP)}
            >
              <Minus className='h-3.5 w-3.5' aria-hidden='true' />
            </button>
            <GraphZoomBadge zoom={zoom} onFit={onFit} />
            <button
              type='button'
              aria-label='Zoom in'
              className={SEGMENT}
              onClick={() => onZoomBy?.(ZOOM_STEP)}
            >
              <Plus className='h-3.5 w-3.5' aria-hidden='true' />
            </button>
          </div>
        </div>
        {/* `bg-primary`: near-black in light, lavender in dark. It is the one
            persistent affordance on an otherwise bare canvas, so it earns the
            weight. */}
        <button
          onClick={onHelp}
          className='group absolute bottom-6 right-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl'
          aria-label='Graph interaction help'
        >
          <HelpCircle className='h-5 w-5 transition-transform group-hover:scale-110' />
        </button>
      </>
    );
  }
  return (
    <div className='absolute right-3 top-3 flex flex-col gap-2'>
      <button
        type='button'
        onClick={onFit}
        aria-label='Fit graph to view'
        className={ROUND}
      >
        <Maximize2 className='h-4 w-4' aria-hidden='true' />
      </button>
      <button
        type='button'
        onClick={onHelp}
        aria-label='Graph interaction help'
        className={ROUND}
      >
        <HelpCircle className='h-4 w-4' aria-hidden='true' />
      </button>
    </div>
  );
}
